import { useState, useEffect } from 'react';
import {
  ShoppingCart,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  FileText,
  Truck,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Package,
  CreditCard,
  Building,
  Grid3X3,
  List
} from 'lucide-react';
import '../styles/OrdenesVenta.css';

const OrdenesVenta = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todas');
  const [currency, setCurrency] = useState('CLP');
  const [viewMode, setViewMode] = useState('table');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const exchangeRates = {
    CLP: 1,
    USD: 950,
    UF: 36500
  };

  const convertCurrency = (amountCLP, toCurrency) => {
    const amount = parseFloat(amountCLP);
    if (toCurrency === 'CLP') {
      return `$${amount.toLocaleString('es-CL')}`;
    } else if (toCurrency === 'USD') {
      const usdAmount = (amount / exchangeRates.USD);
      return `US$${usdAmount.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (toCurrency === 'UF') {
      const ufAmount = (amount / exchangeRates.UF);
      return `${ufAmount.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} UF`;
    }
    return `$${amount.toLocaleString('es-CL')}`;
  };

  const ordenesVenta = [
    {
      id: 'OV-2025-001',
      cotizacion: 'COT-2025-004',
      cliente: 'BHP Billiton',
      fecha: '2025-01-14',
      entrega: '2025-02-14',
      items: 5,
      monto: 123450000,
      margen: '18%',
      estado: 'Completado',
      factura: 'FAC-2025-001',
      ocCliente: 'OC-BHP-4501234',
      descripcion: 'Sistema de filtrado para planta',
      pagado: true
    },
    {
      id: 'OV-2025-002',
      cotizacion: 'COT-2025-001',
      cliente: 'Minera Escondida',
      fecha: '2025-01-14',
      entrega: '2025-02-10',
      items: 15,
      monto: 45678900,
      margen: '12%',
      estado: 'En Proceso',
      factura: 'Pendiente',
      ocCliente: 'OC-ESC-789456',
      descripcion: 'Válvulas alta presión WEIR 3000PSI',
      pagado: false
    },
    {
      id: 'OV-2025-003',
      cotizacion: 'COT-2025-002',
      cliente: 'Codelco División Andina',
      fecha: '2025-01-13',
      entrega: '2025-02-05',
      items: 8,
      monto: 23450000,
      margen: '15%',
      estado: 'Pendiente OC',
      factura: 'Pendiente',
      ocCliente: 'Esperando',
      descripcion: 'Repuestos Komatsu 930E-4',
      pagado: false
    },
    {
      id: 'OV-2025-004',
      cotizacion: 'COT-2025-003',
      cliente: 'Anglo American',
      fecha: '2025-01-12',
      entrega: '2025-01-30',
      items: 23,
      monto: 78900000,
      margen: '10%',
      estado: 'En Tránsito',
      factura: 'FAC-2025-002',
      ocCliente: 'OC-AA-102345',
      descripcion: 'EPP certificado para altura',
      pagado: false
    },
    {
      id: 'OV-2025-005',
      cotizacion: 'COT-2025-005',
      cliente: 'Antofagasta Minerals',
      fecha: '2025-01-10',
      entrega: '2025-02-20',
      items: 12,
      monto: 56780000,
      margen: '14%',
      estado: 'En Proceso',
      factura: 'FAC-2025-003',
      ocCliente: 'OC-AM-567890',
      descripcion: 'Correas transportadoras 72"',
      pagado: false
    }
  ];

  const filteredOrdenes = ordenesVenta.filter(orden => {
    const matchesSearch =
      orden.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      orden.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      orden.descripcion.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'todas' ||
      orden.estado.toLowerCase().replace(' ', '-') === filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'completado': return 'status-completado';
      case 'en proceso': return 'status-proceso';
      case 'pendiente oc': return 'status-pendiente';
      case 'en tránsito': return 'status-transito';
      default: return '';
    }
  };

  const getStatusIcon = (status) => {
    return null; // Sin íconos en los estados
  };

  return (
    <div className="ordenes-venta-container">
      <div className="ordenes-venta-header">
        <div className="header-title">
          <ShoppingCart size={28} />
          <h1>Órdenes de Venta</h1>
        </div>
        <button
          className="btn-nueva-orden"
          style={{
            backgroundColor: themeColor,
            color: '#000',
            border: '2px solid #000'
          }}
        >
          <Plus size={20} />
          Nueva Orden de Venta
        </button>
      </div>

      <div className="ordenes-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Package size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>Órdenes Activas</p>
            <h3>24</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CheckCircle size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>Completadas</p>
            <h3>156</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Clock size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>En Proceso</p>
            <h3>12</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DollarSign size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>Valor Total Mes</p>
            <h3>{convertCurrency(328259800, currency)}</h3>
          </div>
        </div>
      </div>

      <div className="ordenes-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por ID, cliente o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="todas">Todas</option>
            <option value="completado">Completadas</option>
            <option value="en-proceso">En Proceso</option>
            <option value="pendiente-oc">Pendiente OC</option>
            <option value="en-transito">En Tránsito</option>
          </select>
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              style={viewMode === 'table' ? {
                backgroundColor: themeColor,
                color: '#000',
                borderColor: '#000'
              } : {}}
            >
              <List size={16} />
              Tabla
            </button>
            <button
              className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              style={viewMode === 'card' ? {
                backgroundColor: themeColor,
                color: '#000',
                borderColor: '#000'
              } : {}}
            >
              <Grid3X3 size={16} />
              Cards
            </button>
          </div>
          <select
            className="currency-select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="CLP">CLP</option>
            <option value="USD">USD</option>
            <option value="UF">UF</option>
          </select>
          <button className="btn-export">
            <Download size={20} />
            Exportar
          </button>
        </div>
      </div>

{viewMode === 'table' ? (
        <div className="ordenes-table-container">
          <table className="ordenes-table">
            <thead>
              <tr>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>ID Orden</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Cotización</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Cliente</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>OC Cliente</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Descripción</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Items</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Valor</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Margen</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Fecha</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Entrega</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Factura</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Estado</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Pago</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrdenes.map((orden) => (
                <tr key={orden.id}>
                  <td className="id-cell">{orden.id}</td>
                  <td className="ref-cell">{orden.cotizacion}</td>
                  <td>
                    <div className="client-cell">
                      <Building size={16} />
                      {orden.cliente}
                    </div>
                  </td>
                  <td className="oc-cell">
                    {orden.ocCliente === 'Esperando' ? (
                      <span className="oc-pending">Esperando OC</span>
                    ) : (
                      orden.ocCliente
                    )}
                  </td>
                  <td className="description-cell" title={orden.descripcion}>
                    {orden.descripcion.length > 30
                      ? `${orden.descripcion.substring(0, 30)}...`
                      : orden.descripcion}
                  </td>
                  <td className="items-cell">{orden.items}</td>
                  <td className="amount-cell">{convertCurrency(orden.monto, currency)}</td>
                  <td className="margin-cell">{orden.margen}</td>
                  <td>{orden.fecha}</td>
                  <td>{orden.entrega}</td>
                  <td className="factura-cell">
                    {orden.factura === 'Pendiente' ? (
                      <span className="factura-pending">Pendiente</span>
                    ) : (
                      <span className="factura-id">{orden.factura}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status ${getStatusColor(orden.estado)}`}>
                      {getStatusIcon(orden.estado)}
                      {orden.estado}
                    </span>
                  </td>
                  <td>
                    {orden.pagado ? (
                      <span className="pago-status pagado">
                        Pagado
                      </span>
                    ) : (
                      <span className="pago-status pendiente">
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-action" title="Ver">
                        <Eye size={16} />
                      </button>
                      <button className="btn-action" title="Editar">
                        <Edit size={16} />
                      </button>
                      <button className="btn-action" title="Generar Factura">
                        <FileText size={16} />
                      </button>
                      <button className="btn-action btn-track" title="Tracking">
                        <Truck size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ordenes-cards">
          {filteredOrdenes.map((orden) => (
            <div key={orden.id} className="orden-card">
              <div className="card-header">
                <div className="card-id">
                  <h3>{orden.id}</h3>
                  <span className={`status ${getStatusColor(orden.estado)}`}>
                    {getStatusIcon(orden.estado)}
                    {orden.estado}
                  </span>
                </div>
                <div className="card-amount">
                  <span className="amount-value">{convertCurrency(orden.monto, currency)}</span>
                  <span className="margin-value">{orden.margen} margen</span>
                </div>
              </div>
              <div className="card-content">
                <div className="client-info">
                  <div className="client-name">
                    <Building size={16} />
                    <span>{orden.cliente}</span>
                  </div>
                  <div className="ref-info">
                    <span>Cotización: {orden.cotizacion}</span>
                  </div>
                </div>
                <div className="description">
                  <p>{orden.descripcion}</p>
                </div>
                <div className="card-details">
                  <div className="detail-row">
                    <div className="detail-item">
                      <strong>Items:</strong> {orden.items}
                    </div>
                    <div className="detail-item">
                      <Calendar size={14} />
                      <span>Fecha: {orden.fecha}</span>
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <Truck size={14} />
                      <span>Entrega: {orden.entrega}</span>
                    </div>
                    <div className="detail-item">
                      <strong>OC:</strong> {orden.ocCliente === 'Esperando' ? 'Esperando OC' : orden.ocCliente}
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <FileText size={14} />
                      <span>Factura: {orden.factura === 'Pendiente' ? 'Pendiente' : orden.factura}</span>
                    </div>
                    <div className="detail-item">
                      {orden.pagado ? (
                        <span className="pago-status pagado">
                          <CreditCard size={14} />
                          Pagado
                        </span>
                      ) : (
                        <span className="pago-status pendiente">
                          <Clock size={14} />
                          Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-actions">
                <button className="btn-action" title="Ver">
                  <Eye size={16} />
                </button>
                <button className="btn-action" title="Editar">
                  <Edit size={16} />
                </button>
                <button className="btn-action" title="Generar Factura">
                  <FileText size={16} />
                </button>
                <button className="btn-action btn-track" title="Tracking">
                  <Truck size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdenesVenta;